/**
 * Super-admin Billing — dedicated view for plan + subscription tracking.
 *
 * Today the data source is the same `/v1/superadmin/societies` list endpoint
 * we use on the Tenants page; this page is a billing-focused re-skin
 * (MRR / ARR / past-due / per-plan rollup / sortable subscription table).
 * Stand-alone Billing nav entry so founders + finance can land directly
 * here without scrolling through Tenants.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CreditCard, AlertTriangle, TrendingUp, IndianRupee, LineChart, Download } from 'lucide-react';
import { toast } from 'sonner';
import { get, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Select } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { EmptyState } from '@/components/ui/empty-state';
import { formatDateTime } from '@/lib/format';
import { downloadAsCsv, csvFilename } from '@/lib/download';

// Catalog-driven: plan codes flow through as `string` (free/lite/pro/enterprise + custom tiers).
type SubscriptionPlan = string;
type SubscriptionPlanStatus = 'active' | 'past_due' | 'cancelled';
type TenantStatus = 'trial' | 'active' | 'suspended' | 'cancelled';

interface SocietyRow {
  id: string;
  name: string;
  jurisdiction_tag: string;
  country: string;
  city: string | null;
  tenant_status: TenantStatus;
  plan: SubscriptionPlan;
  plan_status: SubscriptionPlanStatus;
  plan_started_at: string | null;
  plan_renews_at: string | null;
  plan_price_inr_per_month: number | null;
  plan_seats_units: number | null;
  _count?: { units: number; residents: number; admins?: number };
}

interface ListResponse {
  items: SocietyRow[];
  next_cursor: string | null;
}

// Plan-tier catalog row — shared shape across superadmin UIs.
interface PlanTierCatalogRow {
  code: string;
  label: string;
  description: string | null;
  price_inr_per_month: number;
  seats_cap_units: number | null;
  sort_order: number;
  is_active: boolean;
  tenants_count?: number;
}
interface PlanTierListResponse {
  items: PlanTierCatalogRow[];
}

// Catalog-safe badge variant — custom tier codes fall back to 'muted'.
function getPlanBadgeVariant(code: string): 'muted' | 'info' | 'ok' | 'warn' {
  const KNOWN: Record<string, 'muted' | 'info' | 'ok' | 'warn'> = {
    free: 'muted',
    lite: 'info',
    pro: 'ok',
    enterprise: 'warn'
  };
  return KNOWN[code] ?? 'muted';
}

// Hardcoded fallback if the catalog endpoint fails.
const LEGACY_PLAN_CODES: readonly string[] = ['free', 'lite', 'pro', 'enterprise'];

type PlanFilter = 'all' | SubscriptionPlan;
type StatusFilter = 'all' | SubscriptionPlanStatus;

export function SuperAdminBillingPage(): JSX.Element {
  const [planFilter, setPlanFilter] = useState<PlanFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const list = useQuery({
    queryKey: ['superadmin', 'billing'],
    queryFn: () => get<ListResponse>('/superadmin/societies?limit=100')
  });

  // Plans-as-data catalog — drives filter pills + rollup buckets.
  const tiersQuery = useQuery({
    queryKey: ['superadmin', 'plan-tiers'],
    queryFn: () => get<PlanTierListResponse>('/superadmin/plan-tiers')
  });

  const items = list.data?.items ?? [];

  // Union of catalog codes (sorted by sort_order) + any codes seen on tenants
  // (catches legacy tenants on a deactivated tier).
  const planCodes = useMemo<string[]>(() => {
    const fromCatalog = tiersQuery.data?.items
      ?.slice()
      .sort((a, b) => a.sort_order - b.sort_order || a.code.localeCompare(b.code))
      .map((t) => t.code);
    const base = fromCatalog && fromCatalog.length > 0 ? fromCatalog : [...LEGACY_PLAN_CODES];
    const fromRows = new Set(items.map((s) => s.plan));
    for (const code of fromRows) {
      if (!base.includes(code)) base.push(code);
    }
    return base;
  }, [tiersQuery.data, items]);

  // Filter
  const filtered = items.filter((s) => {
    if (planFilter !== 'all' && s.plan !== planFilter) return false;
    if (statusFilter !== 'all' && s.plan_status !== statusFilter) return false;
    return true;
  });

  // Aggregate (over ALL active tenants, not filtered — KPIs are global)
  const billable = items.filter(
    (s) => s.tenant_status === 'active' && (s.plan_status === 'active' || s.plan_status === 'past_due')
  );
  const mrr = billable.reduce((sum, s) => sum + (s.plan_price_inr_per_month ?? 0), 0);
  const arr = mrr * 12;
  const paidCount = billable.filter((s) => (s.plan_price_inr_per_month ?? 0) > 0).length;
  const freeCount = billable.length - paidCount;
  const pastDue = items.filter((s) => s.plan_status === 'past_due');
  const cancelled = items.filter((s) => s.plan_status === 'cancelled');
  const renewingThisMonth = items.filter((s) => {
    if (!s.plan_renews_at) return false;
    const r = new Date(s.plan_renews_at);
    const now = new Date();
    return r.getFullYear() === now.getFullYear() && r.getMonth() === now.getMonth();
  });

  // ─── CSV export ──────────────────────────────────────────────
  const [exporting, setExporting] = useState(false);
  const handleExportCsv = async (): Promise<void> => {
    setExporting(true);
    try {
      const p = new URLSearchParams();
      if (planFilter !== 'all') p.set('plan', planFilter);
      if (statusFilter !== 'all') p.set('plan_status', statusFilter);
      const url = `/superadmin/subscriptions/export.csv${p.toString() ? `?${p.toString()}` : ''}`;
      const result = await downloadAsCsv(url, csvFilename('subscriptions'));
      toast.success(`Downloaded ${result.rowCount} rows`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const planRollup = planCodes.map((p) => {
    const rows = items.filter((s) => s.plan === p);
    const total = rows.length;
    const revenue = rows
      .filter(
        (s) => s.tenant_status === 'active' && (s.plan_status === 'active' || s.plan_status === 'past_due')
      )
      .reduce((sum, s) => sum + (s.plan_price_inr_per_month ?? 0), 0);
    return { plan: p, total, revenue };
  });

  return (
    <>
      <PageHeader
        title="Billing"
        description="MRR / ARR / past-due alerts and the full subscription list across every HoodDesk tenant."
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => void handleExportCsv()}
            loading={exporting}
            loadingText="Exporting…"
          >
            <Download className="h-4 w-4" /> Export CSV
          </Button>
        }
      />

      <div className="mx-auto w-full max-w-[1400px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        {/* KPI strip */}
        <div className="grid grid-cols-2 gap-vt-3 md:grid-cols-4">
          <KpiTile
            icon={<IndianRupee className="h-5 w-5" strokeWidth={1.75} />}
            label="MRR"
            value={list.isLoading ? '—' : `₹${mrr.toLocaleString('en-IN')}`}
            footer={`${paidCount} paid · ${freeCount} free`}
            tone="primary"
          />
          <KpiTile
            icon={<TrendingUp className="h-5 w-5" strokeWidth={1.75} />}
            label="ARR (12×)"
            value={list.isLoading ? '—' : `₹${arr.toLocaleString('en-IN')}`}
            footer="Projected annualised"
            tone="ok"
          />
          <KpiTile
            icon={<AlertTriangle className="h-5 w-5" strokeWidth={1.75} />}
            label="Past-due"
            value={list.isLoading ? '—' : String(pastDue.length)}
            footer={`${cancelled.length} cancelled`}
            tone="alert"
          />
          <KpiTile
            icon={<CreditCard className="h-5 w-5" strokeWidth={1.75} />}
            label="Renews this month"
            value={list.isLoading ? '—' : String(renewingThisMonth.length)}
            footer="Watch for collection"
            tone="warn"
          />
        </div>

        {/* Revenue trend — historic MRR reconstruction from update_plan audit replay. */}
        <RevenueTrendCard />

        {/* Plan rollup */}
        <Card>
          <CardContent className="space-y-vt-3 p-vt-4">
            <h2 className="text-vt-14 font-semibold text-vt-text">Plan mix</h2>
            <div className="grid grid-cols-2 gap-vt-3 md:grid-cols-4">
              {planRollup.map((p) => (
                <div key={p.plan} className="rounded-vt-sm border border-vt-border bg-vt-surface-2/40 p-vt-3">
                  <div className="flex items-center justify-between">
                    <Badge variant={getPlanBadgeVariant(p.plan)} className="capitalize">
                      {p.plan}
                    </Badge>
                    <span className="font-mono text-vt-18 font-semibold text-vt-text">{p.total}</span>
                  </div>
                  <div className="mt-vt-2 text-vt-12 text-vt-text-soft">
                    {p.revenue > 0 ? `₹${p.revenue.toLocaleString('en-IN')}/mo` : 'no revenue'}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Past-due alert banner */}
        {pastDue.length > 0 ? (
          <div className="flex items-start gap-vt-3 rounded-vt-md border border-[color:var(--vt-alert)] bg-[color:var(--vt-alert-100)]/30 p-vt-3">
            <AlertTriangle className="mt-vt-1 h-5 w-5 text-vt-alert" strokeWidth={1.75} />
            <div className="flex-1">
              <div className="text-vt-14 font-semibold text-vt-text">
                {pastDue.length} tenant{pastDue.length === 1 ? '' : 's'} past-due
              </div>
              <div className="text-vt-12 text-vt-text-muted">
                Follow up for collection. Suspend after grace window.
              </div>
            </div>
          </div>
        ) : null}

        {/* Subscription table */}
        <Card>
          <CardContent className="space-y-vt-3 p-vt-4">
            <div className="flex flex-wrap items-center gap-vt-2">
              <h2 className="text-vt-14 font-semibold text-vt-text">All subscriptions</h2>
              <span className="text-vt-12 text-vt-text-soft">
                {filtered.length} of {items.length}
              </span>
              <div className="ml-auto flex flex-wrap items-center gap-vt-1">
                <span className="text-vt-12 text-vt-text-soft">Plan:</span>
                {(['all', ...planCodes] as PlanFilter[]).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPlanFilter(p)}
                    className={
                      'rounded-vt-pill border px-vt-2 py-[2px] text-vt-12 capitalize transition-colors ' +
                      (planFilter === p
                        ? 'border-vt-primary bg-vt-primary text-vt-on-primary'
                        : 'border-vt-border bg-vt-surface text-vt-text-muted hover:bg-vt-surface-2')
                    }
                  >
                    {p}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-vt-1">
                <span className="text-vt-12 text-vt-text-soft">Status:</span>
                {(['all', 'active', 'past_due', 'cancelled'] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setStatusFilter(s)}
                    className={
                      'rounded-vt-pill border px-vt-2 py-[2px] text-vt-12 capitalize transition-colors ' +
                      (statusFilter === s
                        ? 'border-vt-primary bg-vt-primary text-vt-on-primary'
                        : 'border-vt-border bg-vt-surface text-vt-text-muted hover:bg-vt-surface-2')
                    }
                  >
                    {s.replace('_', ' ')}
                  </button>
                ))}
              </div>
            </div>

            {list.isLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : filtered.length === 0 ? (
              <EmptyState title="No subscriptions match" description="Clear filters to see all tenants." />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Price / mo</TableHead>
                    <TableHead className="text-right">Seats</TableHead>
                    <TableHead className="text-right">Units used</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead>Renews</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((s) => {
                    const utilization =
                      s.plan_seats_units && s._count
                        ? Math.round(((s._count.units ?? 0) / s.plan_seats_units) * 100)
                        : null;
                    return (
                      <TableRow key={s.id}>
                        <TableCell>
                          <Link
                            to="/superadmin/tenants/$id"
                            params={{ id: s.id }}
                            className="font-medium text-vt-text hover:text-vt-primary"
                          >
                            {s.name}
                          </Link>
                          <div className="text-vt-12 text-vt-text-soft">
                            {s.city ?? '—'} · {s.country}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={getPlanBadgeVariant(s.plan)} className="capitalize">
                            {s.plan}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              s.plan_status === 'active'
                                ? 'ok'
                                : s.plan_status === 'past_due'
                                  ? 'alert'
                                  : 'muted'
                            }
                            className="capitalize"
                          >
                            {s.plan_status.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono text-vt-13">
                          {s.plan_price_inr_per_month === null
                            ? 'Contact'
                            : s.plan_price_inr_per_month === 0
                              ? 'Free'
                              : `₹${s.plan_price_inr_per_month.toLocaleString('en-IN')}`}
                        </TableCell>
                        <TableCell className="text-right font-mono text-vt-13">
                          {s.plan_seats_units ?? '∞'}
                        </TableCell>
                        <TableCell className="text-right font-mono text-vt-13">
                          {s._count?.units ?? 0}
                          {utilization !== null ? (
                            <span
                              className={
                                'ml-vt-1 text-vt-12 ' +
                                (utilization >= 90 ? 'text-vt-alert' : 'text-vt-text-soft')
                              }
                            >
                              ({utilization}%)
                            </span>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-vt-text-muted">
                          {s.plan_started_at ? formatDateTime(s.plan_started_at).split(',')[0] : '—'}
                        </TableCell>
                        <TableCell className="text-vt-text-muted">
                          {s.plan_renews_at ? formatDateTime(s.plan_renews_at).split(',')[0] : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function KpiTile({
  icon,
  label,
  value,
  footer,
  tone
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  footer: string;
  tone: 'primary' | 'ok' | 'alert' | 'warn';
}): JSX.Element {
  const bg: Record<typeof tone, string> = {
    primary: 'bg-vt-primary-100 text-vt-primary',
    ok: 'bg-[color:var(--vt-safe-100)] text-[color:var(--vt-safe)]',
    alert: 'bg-[color:var(--vt-alert-100)] text-[color:var(--vt-alert)]',
    warn: 'bg-[color:var(--vt-warn-100)] text-[color:var(--vt-warn)]'
  };
  return (
    <Card>
      <CardContent className="flex items-center gap-vt-3 p-vt-4">
        <span className={'flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md ' + bg[tone]}>
          {icon}
        </span>
        <div className="min-w-0">
          <div className="truncate text-vt-12 text-vt-text-muted">{label}</div>
          <div className="font-mono text-vt-20 font-semibold text-vt-text">{value}</div>
          <div className="truncate text-vt-12 text-vt-text-soft">{footer}</div>
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Revenue trend (historic MRR by bucket)
//
// Inline by design — single-metric SVG (MRR per bucket) doesn't justify
// extracting into a shared component. Mirrors the look of UsageTrendCard
// (window selector top-right, pure-SVG bar chart, summary footer) but
// drops the per-metric tab strip since there is only one series.
// ──────────────────────────────────────────────────────────────────────

interface RevenueTrendRow {
  bucket_start: string;
  mrr_inr: number;
  arr_inr: number;
  paid_tenants: number;
}

interface RevenueTrendResponse {
  from: string;
  to: string;
  bucket: 'day' | 'week';
  series: RevenueTrendRow[];
  totals: {
    current_mrr_inr: number;
    starting_mrr_inr: number;
    growth_pct: number;
    peak_mrr_inr: number;
    peak_on: string | null;
  };
}

type RevenueWindow = 30 | 90;
type RevenueBucketChoice = 'auto' | 'day' | 'week';

function RevenueTrendCard(): JSX.Element {
  const [windowDays, setWindowDays] = useState<RevenueWindow>(90);
  const [bucketChoice, setBucketChoice] = useState<RevenueBucketChoice>('auto');

  const { from, to } = useMemo(() => {
    const now = new Date();
    const toDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
    const fromDate = new Date(toDate.getTime() - windowDays * 24 * 60 * 60 * 1000);
    return { from: fromDate.toISOString(), to: toDate.toISOString() };
  }, [windowDays]);

  const query = useQuery<RevenueTrendResponse>({
    queryKey: ['superadmin', 'revenue-trends', windowDays, bucketChoice],
    queryFn: () => {
      const qs = new URLSearchParams({ from, to });
      if (bucketChoice !== 'auto') qs.set('bucket', bucketChoice);
      return get<RevenueTrendResponse>(`/superadmin/revenue-trends?${qs.toString()}`);
    }
  });

  const series = query.data?.series ?? [];
  const values = series.map((r) => r.mrr_inr);
  const max = values.length > 0 ? Math.max(...values, 1) : 1;
  const allZero = values.length === 0 || values.every((v) => v === 0);
  const totals = query.data?.totals;
  const paidToday = series.length > 0 ? series[series.length - 1]!.paid_tenants : 0;

  return (
    <Card>
      <CardContent className="space-y-vt-3 p-vt-4">
        <div className="flex flex-wrap items-center justify-between gap-vt-2">
          <div className="flex items-center gap-vt-2">
            <LineChart className="h-4 w-4 text-vt-text-muted" strokeWidth={1.75} />
            <h2 className="text-vt-14 font-semibold text-vt-text">Revenue trend</h2>
            <span className="text-vt-12 text-vt-text-soft">
              {query.data
                ? `${series.length} ${query.data.bucket === 'day' ? 'days' : 'weeks'}`
                : ''}
            </span>
          </div>
          <div className="flex items-center gap-vt-2">
            <Select
              aria-label="Revenue trend window"
              value={String(windowDays)}
              onChange={(e) => setWindowDays(Number.parseInt(e.target.value, 10) as RevenueWindow)}
              className="md:w-40"
            >
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
            </Select>
            <Select
              aria-label="Revenue trend bucket"
              value={bucketChoice}
              onChange={(e) => setBucketChoice(e.target.value as RevenueBucketChoice)}
              className="md:w-28"
            >
              <option value="auto">Auto</option>
              <option value="day">Daily</option>
              <option value="week">Weekly</option>
            </Select>
          </div>
        </div>

        {query.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : query.isError ? (
          <p className="text-vt-13 text-vt-alert" role="alert">
            {getErrorMessage(query.error)}
          </p>
        ) : allZero ? (
          <div className="py-vt-5 text-center text-vt-13 text-vt-text-muted">
            No revenue yet — assign a tenant to a paid tier to see the trend.
          </div>
        ) : (
          <RevenueBars
            values={values}
            max={max}
            labels={series.map((r) => r.bucket_start)}
          />
        )}

        {/* Summary footer */}
        <div className="flex flex-wrap items-center justify-between gap-vt-2 border-t border-vt-border pt-vt-3 text-vt-13">
          <div className="text-vt-text-muted">
            Current MRR{' '}
            <span className="font-mono font-semibold text-vt-text">
              ₹{(totals?.current_mrr_inr ?? 0).toLocaleString('en-IN')}
            </span>{' '}
            · Starting{' '}
            <span className="font-mono font-semibold text-vt-text">
              ₹{(totals?.starting_mrr_inr ?? 0).toLocaleString('en-IN')}
            </span>{' '}
            · Growth{' '}
            <span
              className={
                'font-mono font-semibold ' +
                ((totals?.growth_pct ?? 0) > 0
                  ? 'text-[color:var(--vt-safe)]'
                  : (totals?.growth_pct ?? 0) < 0
                    ? 'text-vt-alert'
                    : 'text-vt-text')
              }
            >
              {(totals?.growth_pct ?? 0) >= 0 ? '+' : ''}
              {(totals?.growth_pct ?? 0).toFixed(1)}%
            </span>
          </div>
          <div className="text-vt-text-soft">
            Peak{' '}
            <span className="font-mono font-semibold text-vt-text">
              ₹{(totals?.peak_mrr_inr ?? 0).toLocaleString('en-IN')}
            </span>
            {totals?.peak_on ? <span className="ml-1">on {totals.peak_on}</span> : null} · Paid tenants
            today:{' '}
            <span className="font-mono font-semibold text-vt-text">{paidToday}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function RevenueBars({
  values,
  max,
  labels
}: {
  values: number[];
  max: number;
  labels: string[];
}): JSX.Element {
  const W = 600;
  const H = 160;
  const PAD_TOP = 8;
  const PAD_BOTTOM = 22;
  const PAD_LEFT = 4;
  const PAD_RIGHT = 4;
  const chartW = W - PAD_LEFT - PAD_RIGHT;
  const chartH = H - PAD_TOP - PAD_BOTTOM;
  const n = values.length;
  const barSlot = chartW / Math.max(n, 1);
  const barW = Math.max(1, barSlot - 2);

  const tickIndices = useMemo(() => {
    if (n === 0) return [] as number[];
    const target = Math.min(6, n);
    if (target === 1) return [0];
    const step = (n - 1) / (target - 1);
    const ticks = new Set<number>();
    for (let i = 0; i < target; i += 1) ticks.add(Math.round(i * step));
    return Array.from(ticks).sort((a, b) => a - b);
  }, [n]);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={`MRR over ${n} buckets`}
      className="h-40 w-full"
    >
      <line
        x1={PAD_LEFT}
        y1={H - PAD_BOTTOM}
        x2={W - PAD_RIGHT}
        y2={H - PAD_BOTTOM}
        stroke="currentColor"
        strokeOpacity="0.18"
        strokeWidth="1"
      />
      {values.map((v, i) => {
        const h = (v / max) * chartH;
        const x = PAD_LEFT + i * barSlot + (barSlot - barW) / 2;
        const y = H - PAD_BOTTOM - h;
        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={barW}
            height={Math.max(h, v === 0 ? 0 : 1)}
            rx={barW > 4 ? 2 : 0}
            className="fill-vt-primary"
            tabIndex={0}
            focusable="true"
          >
            <title>{`${labels[i]} — ₹${v.toLocaleString('en-IN')} MRR`}</title>
          </rect>
        );
      })}
      {tickIndices.map((i) => {
        const x = PAD_LEFT + i * barSlot + barSlot / 2;
        const label = labels[i] ?? '';
        const short = label.length >= 10 ? label.slice(5) : label;
        return (
          <text
            key={`tick-${i}`}
            x={x}
            y={H - 6}
            textAnchor="middle"
            className="fill-current text-vt-text-soft"
            style={{ fontSize: '10px' }}
          >
            {short}
          </text>
        );
      })}
    </svg>
  );
}
